const HUBSPOT_PORTAL_ID = '147679814'
const HUBSPOT_REGION = 'eu1'
const HUBSPOT_EMBED_SRC = 'https://js-eu1.hsforms.net/forms/embed/v2.js'

// HubSpot gives no feedback at all if a submission fails or hangs, so we show our own error after this long.
// A normal submission takes 1–2 s, the timer is paused while a reCAPTCHA challenge is shown.
const SUBMIT_TIMEOUT_MS = 5000
const RENDER_TIMEOUT_MS = 15000

const errorMessages = {
  sv: 'Något gick fel och din förfrågan kunde inte skickas. Ladda om sidan och försök igen, eller kontakta oss på <a href="mailto:kontakt@curoflow.se">kontakt@curoflow.se</a> / 010-750 06 55.',
  en: 'Something went wrong and your request could not be sent. Please reload the page and try again, or contact us at <a href="mailto:kontakt@curoflow.se">kontakt@curoflow.se</a> / +46 10 750 06 55.',
  da: 'Noget gik galt, og din forespørgsel kunne ikke sendes. Genindlæs siden og prøv igen, eller kontakt os på <a href="mailto:kontakt@curoflow.se">kontakt@curoflow.se</a> / +46 10 750 06 55.',
}

let hubspotPromise = null

const pushDataLayer = (event, formId) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, hubspot_form_id: formId })
}

const isRecaptchaChallengeVisible = () => [...document.querySelectorAll('iframe[src*="recaptcha"][src*="bframe"]')]
  .some((iframe) => getComputedStyle(iframe).visibility === 'visible')

/**
 * Reports a form that couldn't be used to our own log (see handle_form_error),
 * since nothing reaches HubSpot when this happens. Uses keepalive so it also
 * gets through when the visitor is leaving the page.
 *
 * @param {string} type - submit_timeout | submit_abandoned | embed_load_failed | render_timeout
 * @param {string} formId - HubSpot form GUID
 */
const logFormError = (type, formId) => {
  const details = [
    `recaptcha_challenge_visible=${isRecaptchaChallengeVisible()}`,
    `grecaptcha=${typeof window.grecaptcha?.enterprise}`,
    `hbspt=${typeof window.hbspt?.forms}`,
    `online=${navigator.onLine}`,
    `consent_marketing=${window.Cookiebot?.consent?.marketing}`,
    `viewport=${window.innerWidth}x${window.innerHeight}`,
  ].join(' ')

  fetch('/wp-json/curoflow/v1/form-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, form_id: formId, page_url: window.location.href, details }),
    keepalive: true,
  }).catch(() => {})
}

/**
 * Loads the HubSpot forms embed script once and resolves with window.hbspt.
 * Reuses the script if a template already printed it.
 *
 * @returns {Promise<object>}
 */
export const loadHubspot = () => {
  if (window.hbspt?.forms) return Promise.resolve(window.hbspt)
  if (hubspotPromise) return hubspotPromise

  hubspotPromise = new Promise((resolve, reject) => {
    let script = document.querySelector('script[src*="hsforms.net/forms/embed/v2.js"]')

    if (!script) {
      script = document.createElement('script')
      script.src = HUBSPOT_EMBED_SRC
      script.charset = 'utf-8'
      script.setAttribute('data-cookieconsent', 'ignore')
      document.head.appendChild(script)
    }

    script.addEventListener('load', () => resolve(window.hbspt))
    script.addEventListener('error', () => {
      hubspotPromise = null
      reject(new Error('HubSpot forms script failed to load'))
    })
  })

  return hubspotPromise
}

const showSubmitError = (container) => {
  if (container.querySelector('.js-hubspot-form-error')) return

  const lang = document.documentElement.lang?.slice(0, 2)
  const el = document.createElement('div')
  el.className = 'js-hubspot-form-error hubspot-form-error'
  el.setAttribute('role', 'alert')
  el.innerHTML = errorMessages[lang] || errorMessages.sv
  container.appendChild(el)
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

/**
 * Watches a rendered HubSpot form and shows an error if a submission
 * neither succeeds nor shows a validation error within SUBMIT_TIMEOUT_MS.
 * Also logs visitors who leave the page while their submission is pending.
 */
const watchSubmission = (container, formId, state) => {
  const form = container.querySelector('form')
  if (!form) return

  // Validation errors are already visible to the user, nothing hangs
  const isPending = () => state.pending && !state.submitted && form.isConnected && !form.querySelector('.hs-error-msgs')

  const check = () => {
    if (!isPending()) return

    // The visitor is solving a reCAPTCHA challenge, give them the time they need
    if (isRecaptchaChallengeVisible()) {
      state.timer = setTimeout(check, SUBMIT_TIMEOUT_MS)
      return
    }

    // Logged now, don't log the same submission again when the visitor leaves
    state.pending = false
    pushDataLayer('hubspot_form_error', formId)
    logFormError('submit_timeout', formId)
    showSubmitError(container)
  }

  form.addEventListener('submit', () => {
    clearTimeout(state.timer)
    container.querySelector('.js-hubspot-form-error')?.remove()
    pushDataLayer('hubspot_form_submit', formId)
    state.pending = true
    state.timer = setTimeout(check, SUBMIT_TIMEOUT_MS)
  })

  window.addEventListener('pagehide', () => {
    if (isPending()) logFormError('submit_abandoned', formId)
  })
}

/**
 * Resolves with the confirmation message HubSpot renders after a submission.
 */
const waitForSubmittedMessage = (container) => new Promise((resolve) => {
  // HubSpot inserts the element before filling it with the message
  const find = () => {
    const message = container.querySelector('.submitted-message')
    return message?.textContent.trim() ? message : null
  }
  if (find()) return resolve(find())

  const observer = new MutationObserver(() => {
    if (!find()) return
    observer.disconnect()
    resolve(find())
  })
  observer.observe(container, { childList: true, subtree: true })
})

/**
 * Creates a HubSpot form in the given target. Call this only once the
 * target is visible, otherwise HubSpot's invisible reCAPTCHA breaks.
 *
 * @param {string} formId - HubSpot form GUID
 * @param {string} target - CSS selector for the container
 * @param {object} [options]
 * @param {(message: HTMLElement) => void} [options.onSubmitted] - Called with HubSpot's confirmation message
 * @returns {Promise<void>}
 */
export const createHubspotForm = async (formId, target, { onSubmitted } = {}) => {
  const hbspt = await loadHubspot().catch((error) => {
    logFormError('embed_load_failed', formId)
    throw error
  })
  const container = document.querySelector(target)
  const state = { submitted: false, timer: null }
  const renderTimer = setTimeout(() => logFormError('render_timeout', formId), RENDER_TIMEOUT_MS)

  hbspt.forms.create({
    portalId: HUBSPOT_PORTAL_ID,
    formId,
    region: HUBSPOT_REGION,
    target,
    // Several pages show the same form twice (form block + footer). Without a unique
    // instance id HubSpot mixes them up: the confirmation and reCAPTCHA end up in the other copy.
    formInstanceId: target.replace(/[^a-z0-9-]/gi, ''),
    onFormReady: () => {
      clearTimeout(renderTimer)
      watchSubmission(container, formId, state)
    },
    onFormSubmitted: async () => {
      state.submitted = true
      clearTimeout(state.timer)
      container.querySelector('.js-hubspot-form-error')?.remove()
      pushDataLayer('hubspot_form_submitted', formId)

      const message = await waitForSubmittedMessage(container)
      onSubmitted?.(message)
    },
  })
}
